import { Group, Paper, SegmentedControl } from "@mantine/core";
import { DateTime } from "luxon";
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  type MRT_TableOptions
} from "mantine-react-table-open";
import { useEffect, useMemo, useState } from "react";
import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router";
import { useDirektvermarkters } from "../../hooks/useDirektvermarkters";
import { useOperators } from "../../hooks/useOperators";
import { getDefaultMRTOptions } from "../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { getDateEndColumn } from "../../mantine/mrt/components/columns/getDateEndColumn";
import { getDateStartColumn } from "../../mantine/mrt/components/columns/getDateStartColumn";
import { getPersonColumn } from "../../mantine/mrt/components/columns/getPersonColumn";
import { MRT_TableDetailPanelWrapper } from "../../mantine/mrt/components/other/MRT_TableDetailPanelWrapper";
import { useToast } from "../../mantine/notifications/useToast";
import { OperatorFeatures } from "../../urls";
import { emptyArray } from "../../utils/constants";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { isDateInFutureDuration } from "../../utils/dates/isDateInFutureDuration";
import { MultiConfirmationModal } from "../BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import { PageContent } from "../BuildingBlocks/Layout/PageContent/PageContent";
import { PageHeader } from "../BuildingBlocks/Layout/PageHeader/PageHeader";
import { NoDataMessage } from "../BuildingBlocks/NoDataMessage/NoDataMessage";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { NoEntries } from "../NoEntries/NoEntries";
import { getDirektvermarkterColumn } from "./columns/getDirektvermarkterColumn";
import { getGeneratorsColumn } from "./columns/getGeneratorsColumn";
import type { DirektvermarktungContractOverviewResponse } from "./Direktvermarktung.types";
import { DirektvermarktungContractExtensionModal } from "./DirektVermarktungContractExtensionModal/DirektvermarktungContractExtensionModal";
import { DirektvermarktungContractInformation } from "./DirektvermarktungContractInformation/DirektvermarktungContractInformation";
import { DirektvermarktungContractModal } from "./DirektvermarktungContractModal/DirektvermarktungContractModal";
import { DirektvermarktungMissingAssignmentToContractAlert } from "./DirektvermarktungMissingAssignmentToContractAlert/DirektvermarktungMissingAssignmentToContractAlert";
import { useDirektvermarktungContractMutations } from "./hooks/useDirektvermarktungContractMutations";
import { useDirektvermarktungContracts } from "./hooks/useDirektvermarktungContracts";
import { sortExpiringSoonFirst } from "./utils/sortExpiringSoonFirst";

const EXPIRING_SOON_THRESHOLD = { weeks: 3, days: 2 };
const EXPIRING_SOON_THRESHOLD_FOR_TABLE_ROWS_SORTING = { days: 30 };
const SHOW_EXTENSION_THRESHOLD = { months: 3 };

const quickFilterData = [
  { label: "Aktiv", value: "active" },
  { label: "Abgelaufen", value: "expired" },
  { label: "Zukünftig", value: "upcoming" },
  { label: "Alle", value: "all" }
];

function DirektvermarktungView() {
  const { showErrorToast } = useToast();
  const [quickFilterValue, setQuickFilterValue] =
    useState<(typeof quickFilterData)[number]["value"]>("active");
  const [globalFilter, setGlobalFilter] = useState<string | undefined>();
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractForDeletion, setContractForDeletion] =
    useState<DirektvermarktungContractOverviewResponse | null>(null);
  const [searchParams] = useSearchParams();

  const variantId = searchParams.get("variantId");
  const contractId = searchParams.get("contractId");

  const [contractForExtension, setContractForExtension] =
    useState<DirektvermarktungContractOverviewResponse | null>(null);

  const {
    data: direktvermarktungContracts,
    isFetching: direktvermarktungContractsFetching,
    error: direktvermarktungContractsError
  } = useDirektvermarktungContracts({
    variantId,
    contractId,
    activeOnly: quickFilterValue === "active" ? true : undefined,
    expiredOnly: quickFilterValue === "expired" ? true : undefined,
    upcomingOnly: quickFilterValue === "upcoming" ? true : undefined
  });

  const {
    data: direktvermarkters,
    isLoading: direktvermarkterLoading,
    error: direktvermarkterError
  } = useDirektvermarkters();

  const {
    data: operators,
    isLoading: operatorsLoading,
    error: operatorsError
  } = useOperators({
    activeFeatures: [
      OperatorFeatures.Paragraph6EEG,
      OperatorFeatures.RevenueMonitoring
    ]
  });

  const isLoading =
    direktvermarktungContractsFetching ||
    operatorsLoading ||
    direktvermarkterLoading;
  const error =
    direktvermarktungContractsError || operatorsError || direktvermarkterError;

  // Saving the contracts into a state variable is needed since changes in dependencies for column accessorFns are not triggering data updates.
  // Therefore depending on load speed of `operators` and `direktvermarkters`, those cols might stay empty in the rendered table.
  //    (see also this TanStack Table PR that will fix it: https://github.com/TanStack/table/pull/5582)
  const [
    stableDirektvermarktungContracts,
    setStableDirektvermarktungContracts
  ] = useState<typeof direktvermarktungContracts>(undefined);
  const [stableContractsQuickFilter, setStableContractsQuickFilter] =
    useState(quickFilterValue);
  useEffect(() => {
    if (!isLoading && direktvermarktungContracts) {
      setStableContractsQuickFilter(quickFilterValue);
      setStableDirektvermarktungContracts(
        sortExpiringSoonFirst(
          direktvermarktungContracts,
          EXPIRING_SOON_THRESHOLD_FOR_TABLE_ROWS_SORTING
        )
      );
    }
  }, [isLoading, direktvermarktungContracts, quickFilterValue]);

  const navigate = useNavigate();

  function handleClickEditContract(contractId: string) {
    navigate({
      pathname: `${contractId}`,
      search: searchParams.toString()
    });
  }

  const { direktvermarktungContractDeleteMutation } =
    useDirektvermarktungContractMutations();

  async function deleteContract() {
    if (!contractForDeletion) {
      return;
    }

    const deletePromise = direktvermarktungContractDeleteMutation.mutateAsync(
      contractForDeletion.id,
      {
        onError: (error) => {
          showErrorToast(error);
        },
        onSettled: () => {
          setContractForDeletion(null);
        }
      }
    );

    return Promise.all([deletePromise])
      .then(() => {})
      .catch();
  }

  const columns = useMemo<
    Array<MRT_ColumnDef<DirektvermarktungContractOverviewResponse>>
  >(
    () => [
      {
        header: "Vertragsname",
        accessorKey: "name",
        size: 300,
        grow: false,
        enableCellHoverReveal: true
      },
      getPersonColumn<DirektvermarktungContractOverviewResponse>({
        key: "operator_id",
        options: {
          header: "Betreibergesellschaft",
          size: 300,
          grow: false,
          enableCellHoverReveal: true
        },
        persons: operators || []
      }),
      {
        ...getDirektvermarkterColumn(direktvermarkters || []),
        size: 300,
        grow: false,
        enableCellHoverReveal: true
      },
      getDateStartColumn<DirektvermarktungContractOverviewResponse>({
        key: "first_date",
        options: {
          header: "Vertragsbeginn"
        }
      }),
      getDateEndColumn<DirektvermarktungContractOverviewResponse>({
        key: "last_date",
        options: {
          header: "Vertragsende",
          size: 240,
          grow: false
        },
        showExpiringSoonIndicator: true,
        expiringSoonThresholdDuration: EXPIRING_SOON_THRESHOLD,
        getExpiringSoonOrExpiredTooltipText: (contract) => {
          const lastDate = backendDateOrDateTimeToLuxonDateTimeOrNull(
            contract.last_date
          )?.endOf("day");
          if (lastDate && lastDate >= DateTime.now()) {
            return "Der Vertrag läuft bald ab";
          }
          if (lastDate && lastDate < DateTime.now()) {
            return "Der Vertrag ist abgelaufen";
          }
          return "";
        },
        getShowExtensionButton: (contract) => {
          const today = DateTime.now().startOf("day");
          const startDate = backendDateOrDateTimeToLuxonDateTimeOrNull(
            contract.first_date
          )?.startOf("day");
          const endDate = backendDateOrDateTimeToLuxonDateTimeOrNull(
            contract.last_date
          )?.startOf("day");

          if (!startDate || !endDate || startDate > today) {
            return false;
          }

          if (endDate <= today) {
            return contract.is_extendable;
          }

          if (
            isDateInFutureDuration(endDate.toJSDate(), SHOW_EXTENSION_THRESHOLD)
          ) {
            return contract.is_extendable;
          }

          return false;
        },
        onClickExtend: (contract) => {
          setContractForExtension(contract);
        }
      }),
      {
        ...getGeneratorsColumn(),
        size: 180,
        grow: false,
        enableCellHoverReveal: true
      }
    ],
    [operators, direktvermarkters]
  );

  const defaultTopToolbarActions: Partial<
    MRT_TableOptions<DirektvermarktungContractOverviewResponse>
  > = getMRTTopToolbarCustomActionsRenderer({
    create: {
      text: "Vertrag hinzufügen",
      onClick: () => setContractModalOpen(true)
    },
    delete: { hide: true }
  });

  const mrtTable =
    useMantineReactTable<DirektvermarktungContractOverviewResponse>({
      ...getDefaultMRTOptions({
        initialState: {
          showGlobalFilter: true,
          showColumnFilters: true
        },
        hideBorder: true
      }),
      layoutMode: "grid",
      ...defaultTopToolbarActions,
      autoResetPageIndex: false,
      onGlobalFilterChange: (updater) => {
        setGlobalFilter(updater);
        mrtTable.setPageIndex(0);
      },
      onColumnFiltersChange: (updater) => {
        setColumnFilters(updater);
        mrtTable.setPageIndex(0);
      },
      onSortingChange: (updater) => {
        setSorting(updater);
        mrtTable.setPageIndex(0);
      },
      renderTopToolbarCustomActions: (props) => (
        <Group ps="sm" w="100%" wrap="nowrap">
          <SegmentedControl
            data={quickFilterData}
            style={{ flexShrink: 0 }}
            value={quickFilterValue}
            onChange={(value) => {
              setQuickFilterValue(value);
              mrtTable.setExpanded({});
              mrtTable.setPageIndex(0);
            }}
          />
          {defaultTopToolbarActions.renderTopToolbarCustomActions?.(props)}
        </Group>
      ),
      ...getDefaultMRTRowEditModeOptions({
        rowActions: {
          edit: {
            text: "Vertrag bearbeiten",
            onClick: (row) => {
              mrtTable.setEditingRow(null);
              handleClickEditContract(row.original.id);
            }
          },
          delete: {
            text: "Vertrag löschen",
            onClick: (row) => setContractForDeletion(row.original)
          }
        }
      }),
      renderDetailPanel: ({ row }) => {
        if (
          !row.original ||
          (typeof row.original === "object" &&
            Object.keys(row.original).length === 0)
        ) {
          return;
        }
        return (
          <MRT_TableDetailPanelWrapper>
            <DirektvermarktungContractInformation
              contractId={row.original.id}
            />
          </MRT_TableDetailPanelWrapper>
        );
      },
      renderEmptyRowsFallback: () =>
        (direktvermarktungContracts && direktvermarktungContracts.length > 0) ||
        quickFilterValue === "active" ||
        quickFilterValue === "expired" ||
        quickFilterValue === "upcoming" ? (
          <NoDataMessage message="Keine Einträge gefunden." />
        ) : (
          <NoEntries
            entryType="Vertrag"
            onClickAddEntry={() => setContractModalOpen(true)}
          />
        ),
      enableRowSelection: false,
      enableFilterMatchHighlighting: true,
      enableColumnFilters: true,
      columnFilterDisplayMode: "popover",
      enableFacetedValues: true,
      state: {
        isLoading,
        globalFilter,
        columnFilters,
        sorting
      },
      columns,
      data: stableDirektvermarktungContracts ?? emptyArray
    });

  const singleContractId =
    stableDirektvermarktungContracts?.length === 1
      ? stableDirektvermarktungContracts[0].id
      : null;
  // keyed on the filter too, so a filter switch that lands on the same single contract re-expands it
  useEffect(() => {
    if (singleContractId) {
      mrtTable.setExpanded({ [singleContractId]: true });
    }
  }, [singleContractId, stableContractsQuickFilter, mrtTable]);

  return (
    <PageContent className="DirektvermarktungView">
      <PageHeader title="Vermarktung" />
      <DirektvermarktungContractModal
        isOpen={contractModalOpen}
        onClose={(contractId) => {
          if (contractId) mrtTable.setExpanded({ [contractId]: true });
          setContractModalOpen(false);
        }}
      />
      <Routes>
        <Route element={<EditContractHelper />} path=":contractId" />
      </Routes>
      <Paper mb="md">
        <DirektvermarktungMissingAssignmentToContractAlert />
        <LoadOrError error={error}>
          <MantineReactTable table={mrtTable} />
          {contractForExtension && (
            <DirektvermarktungContractExtensionModal
              contract={contractForExtension}
              onClose={() => setContractForExtension(null)}
            />
          )}
          {contractForDeletion && (
            <MultiConfirmationModal
              actionName="löschen"
              actionObjects={[contractForDeletion.name]}
              confirmationText="Ihr Vertrag kann anschließend nicht wiederhergestellt werden."
              headerText="Wollen Sie diesen Vertrag löschen?"
              isModalOpen={contractForDeletion !== null}
              objectName="Vertrag"
              toggleModal={() => setContractForDeletion(null)}
              onAction={deleteContract}
            />
          )}
        </LoadOrError>
      </Paper>
    </PageContent>
  );
}

function EditContractHelper() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { contractId } = useParams();

  return (
    <DirektvermarktungContractModal
      contractId={contractId}
      isOpen
      onClose={() =>
        navigate({
          pathname: "../",
          search: searchParams.toString()
        })
      }
    />
  );
}

export { DirektvermarktungView };
